// frontend/klaes-app/src/pages/Dashboard/Dashboard.jsx
import { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { usePreferences } from '../../contexts/PreferencesContext';
import { OnlineUsersProvider, useOnlineUsers } from '../../contexts/OnlineUsersContext';
import DashboardHeader from '../../components/DashboardHeader/DashboardHeader';
import TopicCard from '../../components/TopicCard/TopicCard';
import FilmFavs from '../../components/FilmFavs'; 
import MusicFavs from '../../components/MusicFavs';
import BookFavs from '../../components/BookFavs';
import OptionsModal from '../../components/OptionsModal/OptionsModal';
import OnlineUsers from '../../components/OnlineUsers/OnlineUsers';
import styles from './Dashboard.module.css';

// Alle verfügbaren Widgets mit ihren Eigenschaften
const ALL_WIDGETS = {
  'film-favs': { 
    title: 'film-favs', 
    description: 'Lieblingsfilme und Serien verwalten, bewerten und teilen',
    component: FilmFavs
  },
  'music-favs': { 
    title: 'music-favs', 
    description: 'Lieblingsmusik verwalten, bewerten und teilen',
    component: MusicFavs
  },
  'book-favs': {
    title: 'book-favs',
    description: 'Bücher verwalten, bewerten und teilen',
    component: BookFavs
  },
  'family-calendar': { 
    title: 'family-calendar', 
    description: 'Gemeinsame Termine',
    component: null // Platzhalter für zukünftige Implementierung
  },
  'family-chat': { 
    title: 'family-chat', 
    description: 'Chat mit der Familie',
    component: null // Platzhalter für zukünftige Implementierung
  },
  'schwurbler-newsfeed': { 
    title: 'schwurbler-newsfeed', 
    description: 'Newsfeed',
    component: null // Platzhalter für zukünftige Implementierung
  }
};

// Innere Komponente, die den Context nutzt
const DashboardContent = () => {
  const { user } = useAuth();
  const { preferences } = usePreferences();
  const { onlineCount } = useOnlineUsers();
  const [showOptions, setShowOptions] = useState(false);
  const [showOnlineWidget, setShowOnlineWidget] = useState(() => {
    // Aus localStorage laden, standardmäßig false (aus)
    return localStorage.getItem('showOnlineWidget') === 'true';
  });
  const [activeWidget, setActiveWidget] = useState(null); // Für Fullscreen-Ansicht eines Widgets

  const widgetOrder = preferences.widgets?.order ?? Object.keys(ALL_WIDGETS);
  const widgetVisibility = preferences.widgets?.visibility ?? {};
  const visibleWidgets = widgetOrder.filter(id => widgetVisibility[id] !== false);

  const handleOnlineToggle = () => {
    const newValue = !showOnlineWidget;
    setShowOnlineWidget(newValue);
    localStorage.setItem('showOnlineWidget', newValue);
  };

  // Wenn ein Widget aktiv ist, zeige nur dieses Widget im Fullscreen-Modus
  if (activeWidget) {
    const WidgetComponent = ALL_WIDGETS[activeWidget]?.component;
    
    // Falls die Komponente noch nicht implementiert ist, zeige eine Platzhalter-Nachricht
    if (!WidgetComponent) {
      return (
        <div className={styles.container}>
          <DashboardHeader 
            user={user} 
            onOptionsClick={() => setShowOptions(true)}
            onOnlineToggle={handleOnlineToggle}
            showOnlineWidget={showOnlineWidget}
          />
          <div className={styles.widgetFullscreen}>
            <button className={styles.backBtn} onClick={() => setActiveWidget(null)}>
              ← Zurück zum Dashboard
            </button>
            <div className={styles.notImplemented}>
              <h2>{ALL_WIDGETS[activeWidget]?.title}</h2>
              <p>Diese Komponente befindet sich noch in Entwicklung.</p>
            </div>
          </div>
          {showOptions && <OptionsModal onClose={() => setShowOptions(false)} />}
        </div>
      );
    }
    
    return (
      <div className={styles.container}>
        <DashboardHeader 
          user={user} 
          onOptionsClick={() => setShowOptions(true)}
          onOnlineToggle={handleOnlineToggle}
          showOnlineWidget={showOnlineWidget}
        />
        <div className={styles.widgetFullscreen}>
          <button className={styles.backBtn} onClick={() => setActiveWidget(null)}>
            ← Zurück zum Dashboard
          </button>
          <WidgetComponent onNavigateHome={() => setActiveWidget(null)} />
        </div>
        {showOptions && <OptionsModal onClose={() => setShowOptions(false)} />}
      </div>
    );
  }

  // Normaler Dashboard-Modus mit allen Widgets als Kacheln
  return (
    <div className={styles.container}>
      <DashboardHeader 
        user={user} 
        onOptionsClick={() => setShowOptions(true)}
        onOnlineToggle={handleOnlineToggle}
        showOnlineWidget={showOnlineWidget}
      />
      
      <div className={styles.content}>
        {showOnlineWidget && (
          <aside className={styles.sidebar}>
            <OnlineUsers />
          </aside>
        )}
        
        <main className={styles.main}>
          <div className={styles.grid}>
            {visibleWidgets.map(widgetId => {
              const widget = ALL_WIDGETS[widgetId];
              // Wenn es eine spezielle Komponente gibt, zeige eine interaktive Karte
              if (widget.component) {
                return (
                  <div 
                    key={widgetId} 
                    className={styles.widgetCard}
                    onClick={() => setActiveWidget(widgetId)}
                  >
                    <h3>{widget.title}</h3>
                    <p>{widget.description}</p>
                    <button className={styles.openWidgetBtn}>Öffnen →</button>
                  </div>
                );
              }
              // Sonst zeige die normale TopicCard
              return (
                <TopicCard
                  key={widgetId}
                  title={widget.title}
                  description={widget.description}
                />
              );
            })}
          </div>
        </main>
      </div>
      
      {showOptions && <OptionsModal onClose={() => setShowOptions(false)} />}
    </div>
  );
};

// Äußere Komponente mit Provider
const Dashboard = () => {
  return (
    <OnlineUsersProvider>
      <DashboardContent />
    </OnlineUsersProvider>
  );
};

export default Dashboard;